import { useState, useEffect, useCallback } from 'react'
import { GameState } from '../store/types'
import { apiTradeList, apiTradePost, apiTradeBuy, apiTradeCancel } from '../server/ServiceCall'

const GRADE_COLOR: Record<string, string> = {
  COMMON:'#a0a0a0', UNCOMMON:'#60c060', RARE:'#4090e0',
  EPIC:'#a060e0', LEGEND:'#e0a030', MYTH:'#e04060',
}
const CAT_ORDER: Record<string, number> = {
  EQUIP:0, MUGONG_BOOK:1, CONSUME:2, HERB:3, MATERIAL:4,
}
const SLOT_ORDER: Record<string, number> = {
  WEAPON:0, CLOTH:1, HAT:2, INNER:3, CLOAK:4, GLOVE:5, SHOE:6, NECK:7, BRACELET:8,
}
const GRADE_ORDER: Record<string, number> = {
  MYTH:0, LEGEND:1, EPIC:2, RARE:3, UNCOMMON:4, COMMON:5,
}
function cmpItem(a: { category: string; slot?: string | null; grade: string }, b: typeof a) {
  const ka = [(CAT_ORDER[a.category] ?? 9), (SLOT_ORDER[a.slot ?? ''] ?? 9), (GRADE_ORDER[a.grade] ?? 9)]
  const kb = [(CAT_ORDER[b.category] ?? 9), (SLOT_ORDER[b.slot ?? ''] ?? 9), (GRADE_ORDER[b.grade] ?? 9)]
  for (let i = 0; i < ka.length; i++) if (ka[i] !== kb[i]) return ka[i] - kb[i]
  return 0
}

interface TradeOrder {
  orderId: number; sellerName: string; isMine: boolean
  itemId: string; itemName: string; grade: string
  qty: number; priceEach: number; totalPrice: number; regDate: string
}

interface Props { gs: GameState; token: string; onRefresh: () => void }

export default function TabTrade({ gs, token, onRefresh }: Props) {
  const [orders,   setOrders]   = useState<TradeOrder[]>([])
  const [loading,  setLoading]  = useState(false)
  const [view,     setView]     = useState<'list'|'post'>('list')
  const [selOrder, setSelOrder] = useState<TradeOrder | null>(null)
  const [postItem, setPostItem] = useState<number | null>(null)  // invId
  const [postQty,  setPostQty]  = useState(1)
  const [postPrice,setPostPrice]= useState(100)
  const [busy,     setBusy]     = useState(false)
  const [msg,      setMsg]      = useState('')
  const [msgOk,    setMsgOk]    = useState(false)

  function showMsg(m: string, ok = false) { setMsg(m); setMsgOk(ok); setTimeout(() => setMsg(''), 3000) }

  const loadOrders = useCallback(() => {
    setLoading(true)
    apiTradeList(token)
      .then(res => {
        if (res.resultCode === '00') {
          const raw = JSON.parse(res.resultData || '[]') as any[]
          setOrders(raw.map(r => ({
            orderId:    Number(r.ORDER_ID),
            sellerName: String(r.SELLER_NAME),
            isMine:     Number(r.IS_MINE) === 1,
            itemId:     String(r.ITEM_ID),
            itemName:   String(r.ITEM_NAME),
            grade:      String(r.GRADE),
            qty:        Number(r.QTY),
            priceEach:  Number(r.PRICE_EACH),
            totalPrice: Number(r.TOTAL_PRICE),
            regDate:    String(r.REG_DATE ?? ''),
          })))
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [token])

  useEffect(() => { loadOrders() }, [loadOrders])

  async function doBuy(order: TradeOrder) {
    if (!window.confirm(`[${order.itemName}] × ${order.qty}개를 ${order.totalPrice.toLocaleString()}전에 구매하겠습니까?`)) return
    setBusy(true)
    try {
      const res = await apiTradeBuy(token, order.orderId)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { loadOrders(); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doCancel(order: TradeOrder) {
    setBusy(true)
    try {
      const res = await apiTradeCancel(token, order.orderId)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { loadOrders(); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doPost() {
    if (!postItem) return
    setBusy(true)
    try {
      const res = await apiTradePost(token, postItem, postQty, postPrice)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { setView('list'); setPostItem(null); loadOrders(); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  const myOrderCount = orders.filter(o => o.isMine).length
  const bagItems = gs.inventory
    .filter(item => !item.equipSlot)
    .sort((a, b) => cmpItem(a, b))
  const selectedInv  = bagItems.find(i => i.invId === postItem)

  return (
    <div className="tab-action">
      {msg && <div className={`inven-msg${msgOk ? ' ok' : ''}`}>{msg}</div>}

      {/* 뷰 전환 */}
      <div className="inven-view-tabs">
        <button className={`inven-view-btn${view==='list'?' active':''}`}
          onClick={() => { setView('list'); loadOrders() }}>
          거래 목록 ({orders.length})
        </button>
        <button className={`inven-view-btn${view==='post'?' active':''}`}
          onClick={() => setView('post')}>
          물건 올리기 ({myOrderCount}/10)
        </button>
      </div>

      {/* 거래 목록 */}
      {view === 'list' && (
        <>
          {loading ? (
            <div style={{ color:'#605040', textAlign:'center', padding:'30px 0' }}>불러오는 중...</div>
          ) : orders.length === 0 ? (
            <div className="bag-empty">등록된 거래가 없습니다.</div>
          ) : (
            <div className="bag-list">
              {orders.map(order => (
                <div key={order.orderId}
                  className={`bag-item${selOrder?.orderId===order.orderId?' selected':''}`}
                  onClick={() => setSelOrder(selOrder?.orderId===order.orderId ? null : order)}
                >
                  <span className="bag-grade-dot" style={{ background: GRADE_COLOR[order.grade] }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <span className="bag-item-name">{order.itemName}</span>
                      {order.qty > 1 && <span className="bag-item-qty">×{order.qty}</span>}
                      {order.isMine && <span style={{ fontSize:9, color:'#c8820a', border:'1px solid #6a4010', padding:'0 3px', borderRadius:2 }}>내 물건</span>}
                    </div>
                    <div style={{ fontSize:10, color:'#806050', marginTop:1 }}>
                      판매자: {order.sellerName}
                    </div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ color:'#e0c040', fontWeight:'bold', fontSize:12 }}>
                      {order.totalPrice.toLocaleString()}전
                    </div>
                    {order.qty > 1 && (
                      <div style={{ fontSize:10, color:'#706050' }}>
                        개당 {order.priceEach.toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 선택 주문 액션 */}
          {selOrder && (
            <div className="item-action-panel">
              <div className="item-action-title" style={{ color: GRADE_COLOR[selOrder.grade] }}>
                {selOrder.itemName}
              </div>
              <div className="item-action-info">
                <span>{selOrder.qty}개</span>
                <span style={{ color:'#e0c040' }}>합계 {selOrder.totalPrice.toLocaleString()}전</span>
                <span>보유 {gs.char.gold.toLocaleString()}전</span>
              </div>
              <div className="item-action-btns">
                {selOrder.isMine ? (
                  <button className="action-btn drop" disabled={busy} onClick={() => doCancel(selOrder)}>
                    등록 취소
                  </button>
                ) : (
                  <button className="action-btn equip"
                    disabled={busy || gs.char.gold < selOrder.totalPrice}
                    onClick={() => doBuy(selOrder)}>
                    구매하기
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* 물건 올리기 */}
      {view === 'post' && (
        <section className="card-section">
          <div className="card-title">거래소에 올릴 아이템 선택</div>
          {bagItems.length === 0 ? (
            <div className="bag-empty">올릴 아이템이 없습니다.</div>
          ) : (
            <>
              <div className="bag-list" style={{ maxHeight:200, overflowY:'auto', marginBottom:10 }}>
                {bagItems.map(item => (
                  <div key={item.invId}
                    className={`bag-item${postItem===item.invId?' selected':''}`}
                    onClick={() => { setPostItem(postItem===item.invId ? null : item.invId); setPostQty(1) }}
                  >
                    <span className="bag-grade-dot" style={{ background: GRADE_COLOR[item.grade] }} />
                    <span className="bag-item-name">{item.name}</span>
                    {item.qty > 1 && <span className="bag-item-qty">×{item.qty}</span>}
                  </div>
                ))}
              </div>

              {postItem && selectedInv && (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  <div style={{ fontSize:12, color:'#c8b890', fontWeight:'bold' }}>
                    {selectedInv.name}
                  </div>
                  {!['EQUIP','MUGONG_BOOK'].includes(selectedInv.category) && (
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <label style={{ fontSize:11, color:'#806050', width:40 }}>수량</label>
                    <input type="number" className="drop-qty-input" style={{ width:60 }}
                      min={1} max={selectedInv.qty} value={postQty}
                      onChange={e => setPostQty(Math.max(1, Math.min(selectedInv.qty, Number(e.target.value))))} />
                  </div>
                  )}
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <label style={{ fontSize:11, color:'#806050', width:40 }}>가격</label>
                    <input type="number" className="drop-qty-input" style={{ width:100 }}
                      min={1} value={postPrice}
                      onChange={e => setPostPrice(Math.max(1, Number(e.target.value)))} />
                    <span style={{ fontSize:11, color:'#806050' }}>전 / 개</span>
                  </div>
                  <div style={{ fontSize:11, color:'#a09060' }}>
                    합계 {(postPrice * postQty).toLocaleString()}전
                  </div>
                  <button className="action-btn equip" disabled={busy} onClick={doPost}>
                    거래소 등록
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  )
}
